import React, { useState,useEffect } from 'react';
import { Save, User, Heart, Activity, Sliders, AlertCircle } from 'lucide-react';
import Navigation from '../components/common/Navigation';
import api from '../request'; // Axios instance
import ToastMessage from '../components/ToastMessage'; // Custom toast component
const UserSettings = () => {
  const [activeTab, setActiveTab] = useState('demographic');
  
  // Mock data - would come from context or state management in a real app
  const [demographicData, setDemographicData] = useState({
    firstName: 'John',
    lastName: 'Doe',
    email: 'john.doe@example.com',
    city: 'New York',
    dateOfBirth: '1990-01-01',
    gender: 'male',
    occupation: 'Office Worker',
    educationLevel: 'bachelor'
  });


  const [toast, setToast] = useState({ message: '', type: '' });
  
  const showToast = (msg, type = 'success') => {
      setToast({ message: msg, type });
      setTimeout(() => setToast({ message: '', type: '' }), 4000);
  };
  
  const [physicalData, setPhysicalData] = useState({
    height: 175, // in cm
    weight: 70, // in kg
    bodyFat: 18, // in percentage
    restingHeartRate: 68, // in bpm
    walkingPace: 'moderate', // slow, moderate, brisk
    strideLength: 75, // in cm
    preferredDistance: 5, // in km
  });
  
  const [healthData, setHealthData] = useState({
    conditions: {
      heartDisease: false,
      highBloodPressure: false,
      diabetes: false,
      arthritis: false,
      respiratoryIssues: false,
      none: true
    },
    sleepHours: 7,
    waterIntake: 2000, // in ml
    medications: '',
    allergies: '',
    injuries: ''
  });
  
  const [preferenceData, setPreferenceData] = useState({
    timePreference: 'morning', // morning, afternoon, evening
    environmentPreference: 'outdoors', // outdoors, indoors, mixed
    goalType: 'distance', // steps, distance, time, calories
    socialPreference: 'solo', // solo, group, both
    challengePreference: 'competitive', // casual, competitive, both
    equipment: {
      smartwatch: true,
      fitnessTracker: false,
      smartphone: true,
      specializedFootwear: false
    }
  });
  

  // Fetch user data on mount
  useEffect(() => {
    const loadUserSettings = async () => {
      try {
        const response = await api.get('/user');
        const user = response.data;

        // If first_name and last_name are null, derive them from name
        let firstName = user.first_name || '';
        let lastName = user.last_name || '';

        if (!firstName && !lastName && user.name) {
          const nameParts = user.name.trim().split(' ');
          firstName = nameParts[0] || '';
          lastName = nameParts.slice(1).join(' ') || '';
        }

        setDemographicData({
          firstName,
          lastName,
          email: user.email || '',
          city: user.city || '',
          dateOfBirth: user.date_of_birth || '',
          gender: user.gender || '',
          occupation: user.occupation || '',
          educationLevel: user.education_level || ''
        });

        setPhysicalData({
          height: user.height || 0,
          weight: user.weight || 0,
          bodyFat: user.body_fat || 0,
          restingHeartRate: user.resting_heart_rate || 0,
          walkingPace: user.walking_pace || '',
          strideLength: user.stride_length || 0,
          preferredDistance: user.preferred_distance || 0
        });

        setHealthData({
          conditions: user.health_conditions || {},
          sleepHours: user.sleep_hours || 7,
          waterIntake: user.water_intake || 2000,
          medications: user.medications || '',
          allergies: user.allergies || '',
          injuries: user.injuries || ''
        });

        setPreferenceData({
          timePreference: user.time_preference || 'morning',
          environmentPreference: user.environment_preference || 'outdoors',
          goalType: user.goal_type || 'steps',
          socialPreference: user.social_preference || 'solo',
          challengePreference: user.challenge_preference || 'casual',
          equipment: user.equipment || {}
        });
      } catch (error) {
        showToast('Failed to load settings', 'error');
      }
    };

    loadUserSettings();
  }, []);

  // Handle form updates
  const handleDemographicChange = (e) => {
    setDemographicData({
      ...demographicData,
      [e.target.name]: e.target.value
    });
  };
  
  const handlePhysicalChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setPhysicalData({
      ...physicalData,
      [e.target.name]: value
    });
  };
  
  const handleHealthConditionChange = (condition) => {
    const newConditions = { ...healthData.conditions };
    
    // If selecting "none", uncheck all others
    if (condition === 'none') {
      Object.keys(newConditions).forEach(key => {
        newConditions[key] = key === 'none';
      });
    } else {
      // If selecting a condition, uncheck "none"
      newConditions[condition] = !newConditions[condition];
      newConditions.none = false;
    }
    
    setHealthData({
      ...healthData,
      conditions: newConditions
    });
  };
  
  const handleHealthChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setHealthData({
      ...healthData,
      [e.target.name]: value
    });
  };
  
  const handlePreferenceChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setPreferenceData({
      ...preferenceData,
      [e.target.name]: value
    });
  };
  
  const handleEquipmentChange = (item) => {
    setPreferenceData({
      ...preferenceData,
      equipment: {
        ...preferenceData.equipment,
        [item]: !preferenceData.equipment[item]
      }
    });
  };
  
  // Calculate BMI for physical data tab
 const calculateBMI = () => {
    const heightInMeters = physicalData.height / 100;
    const bmi = physicalData.weight / (heightInMeters * heightInMeters);

    // Check if BMI is a valid number
    return isNaN(bmi) || !isFinite(bmi) ? 0 : bmi.toFixed(1);
  };
  
  const getBMICategory = (bmi) => {
    if (bmi < 18.5) return { label: 'Underweight', color: 'text-blue-500' };
    if (bmi < 25) return { label: 'Normal', color: 'text-green-500' };
    if (bmi < 30) return { label: 'Overweight', color: 'text-yellow-500' };
    return { label: 'Obese', color: 'text-red-500' };
  };
  
  const bmi = calculateBMI();
  const bmiCategory = getBMICategory(bmi);
  
  // Save settings (would connect to API in real app)
  const handleSaveSettings = async () => {
    try {
      // const payload = {
      //   ...demographicData,
      //   ...physicalData,
      //   ...healthData,
      //   ...preferenceData,
      //   health_conditions: healthData.conditions,
      //   equipment: preferenceData.equipment
      // };

      const payload = {
        // Demographic Data
        first_name: demographicData.firstName,
        last_name: demographicData.lastName,
        email: demographicData.email,
        city: demographicData.city,
        date_of_birth: demographicData.dateOfBirth,
        gender: demographicData.gender,
        occupation: demographicData.occupation,
        education_level: demographicData.educationLevel,

        // Physical Data (snake_case mapping)
        height: physicalData.height,
        weight: physicalData.weight,
        body_fat: physicalData.bodyFat,
        resting_heart_rate: physicalData.restingHeartRate,
        walking_pace: physicalData.walkingPace,
        stride_length: physicalData.strideLength,
        preferred_distance: physicalData.preferredDistance,

        // Health Data
        health_conditions: healthData.conditions,
        sleep_hours: parseInt(healthData.sleepHours),
        water_intake: healthData.waterIntake,
        medications: healthData.medications,
        allergies: healthData.allergies,
        injuries: healthData.injuries,

        // Preference Data
        time_preference: preferenceData.timePreference,
        environment_preference: preferenceData.environmentPreference,
        goal_type: preferenceData.goalType,
        social_preference: preferenceData.socialPreference,
        challenge_preference: preferenceData.challengePreference,
        equipment: preferenceData.equipment
      };

      await api.put('/user/settings', payload);
      showToast('Settings saved successfully!');
    } catch (error) {
      showToast('Error saving settings', 'error');
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
        {/* <div className="hidden md:flex flex-col w-64 bg-white border-r p-4">
        <div className="text-2xl font-bold text-primary mb-8">Stepping4Life</div> */}
              <ToastMessage message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: '' })} />

        <nav className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 bg-white shadow-sm z-20 flex-col">
      <Navigation />
      </nav>
      
        
        {/* <div className="mt-auto pt-4 border-t">
          <div className="flex items-center p-4">
            <img src="/api/placeholder/40/40" alt="Profile" className="w-10 h-10 rounded-full mr-3" />
            <div>
              <p className="font-medium">John Walker</p>
              <p className="text-sm text-gray-500">Premium Member</p>
            </div>
          </div>
        </div> */}
      {/* </div> */}
      {/* Header */}
      <div className="flex-1 flex flex-col">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl lg:ml-64 mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl font-semibold text-gray-900">User Settings</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your personal information and preferences
          </p>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-7xl lg:ml-64 mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8 w-full justify-between" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('demographic')}
              className={`flex items-center pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'demographic'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <User className="mr-2 h-5 w-5" />
            <span className='hidden md:flex lg:flex'> Demographic Data</span> 
            </button>
            
            <button
              onClick={() => setActiveTab('physical')}
              className={`flex items-center pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'physical'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Activity className="mr-2 h-5 w-5" />
              <span className='hidden md:flex lg:flex'> Physical & Fitness Data</span>
            </button>
            
            <button
              onClick={() => setActiveTab('health')}
              className={`flex items-center pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'health'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Heart className="mr-2 h-5 w-5" />
              <span className='hidden md:flex lg:flex'> Health & Medical Data</span>
            </button>
            
            <button
              onClick={() => setActiveTab('preferences')}
              className={`flex items-center pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'preferences'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Sliders className="mr-2 h-5 w-5" />
              <span className='hidden md:flex lg:flex'> Walking & Activity Preferences</span>
            </button>
          </nav>
        </div>
        
        {/* Tab Content */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          {/* Demographic Data Tab */}
          {activeTab === 'demographic' && (
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900">Personal Information</h2>
              <p className="text-sm text-gray-500">
                This information helps us personalize your experience.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    id="firstName"
                    value={demographicData.firstName}
                    onChange={handleDemographicChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
                
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    id="lastName"
                    value={demographicData.lastName}
                    onChange={handleDemographicChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={demographicData.email}
                    onChange={handleDemographicChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
                
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    id="city"
                    value={demographicData.city}
                    onChange={handleDemographicChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
                
                <div>
                  <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    id="dateOfBirth"
                    value={demographicData.dateOfBirth}
                    onChange={handleDemographicChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
                
                <div>
                  <label htmlFor="gender" className="block text-sm font-medium text-gray-700">
                    Gender
                  </label>
                  <select
                    id="gender"
                    name="gender"
                    value={demographicData.gender}
                    onChange={handleDemographicChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="non-binary">Non-binary</option>
                    <option value="prefer-not-to-say">Prefer not to say</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="occupation" className="block text-sm font-medium text-gray-700">
                    Occupation
                  </label>
                  <input
                    type="text"
                    name="occupation"
                    id="occupation"
                    value={demographicData.occupation}
                    onChange={handleDemographicChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
                
                <div>
                  <label htmlFor="educationLevel" className="block text-sm font-medium text-gray-700">
                    Education Level
                  </label>
                  <select
                    id="educationLevel"
                    name="educationLevel"
                    value={demographicData.educationLevel}
                    onChange={handleDemographicChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  >
                    <option value="highschool">High School</option>
                    <option value="associate">Associate's Degree</option>
                    <option value="bachelor">Bachelor's Degree</option>
                    <option value="master">Master's Degree</option>
                    <option value="doctoral">Doctoral Degree</option>
                  </select>
                </div>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">Privacy Information</h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <p>
                        Your personal information is securely stored and never shared with third parties.
                        You can request data deletion at any time.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Physical & Fitness Data Tab */}
          {activeTab === 'physical' && (
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900">Physical Measurements</h2>
              <p className="text-sm text-gray-500">
                These measurements help us calculate your fitness metrics and recommendations.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="height" className="block text-sm font-medium text-gray-700">
                    Height (cm)
                  </label>
                  <input
                    type="number"
                    name="height"
                    id="height"
                    value={physicalData.height}
                    onChange={handlePhysicalChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
                
                <div>
                  <label htmlFor="weight" className="block text-sm font-medium text-gray-700">
                    Weight (kg)
                  </label>
                  <input
                    type="number"
                    name="weight"
                    id="weight"
                    value={physicalData.weight}
                    onChange={handlePhysicalChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
                
                {/* BMI Calculator */}
                <div className="md:col-span-2">
                  <div className="bg-gray-50 p-4 rounded-md">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">BMI Calculator</h3>
                    <div className="flex items-center">
                      <div className="text-2xl font-bold">{bmi}</div>
                      <div className={`ml-3 text-sm font-medium ${bmiCategory.color}`}>
                        {bmiCategory.label}
                      </div>
                    </div>
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="h-2.5 rounded-full bg-gradient-to-r from-blue-500 via-green-500 to-red-500"
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>Underweight</span>
                      <span>Normal</span>
                      <span>Overweight</span>
                      <span>Obese</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="bodyFat" className="block text-sm font-medium text-gray-700">
                    Body Fat Percentage (optional)
                  </label>
                  <input
                    type="number"
                    name="bodyFat"
                    id="bodyFat"
                    value={physicalData.bodyFat}
                    onChange={handlePhysicalChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
                
                <div>
                  <label htmlFor="restingHeartRate" className="block text-sm font-medium text-gray-700">
                    Resting Heart Rate (bpm)
                  </label>
                  <input
                    type="number"
                    name="restingHeartRate"
                    id="restingHeartRate"
                    value={physicalData.restingHeartRate}
                    onChange={handlePhysicalChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
                
                <div>
                  <label htmlFor="walkingPace" className="block text-sm font-medium text-gray-700">
                    Walking Pace
                  </label>
                  <select
                    id="walkingPace"
                    name="walkingPace"
                    value={physicalData.walkingPace}
                    onChange={handlePhysicalChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  >
                    <option value="slow">Slow (less than 3 km/h)</option>
                    <option value="moderate">Moderate (3-5 km/h)</option>
                    <option value="brisk">Brisk (more than 5 km/h)</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="strideLength" className="block text-sm font-medium text-gray-700">
                    Stride Length (cm)
                  </label>
                  <input
                    type="number"
                    name="strideLength"
                    id="strideLength"
                    value={physicalData.strideLength}
                    onChange={handlePhysicalChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Average stride length for adults is 70-80 cm
                  </p>
                </div>
                
                <div>
                  <label htmlFor="preferredDistance" className="block text-sm font-medium text-gray-700">
                    Preferred Walking Distance (km)
                  </label>
                  <input
                    type="number"
                    name="preferredDistance"
                    id="preferredDistance"
                    value={physicalData.preferredDistance}
                    onChange={handlePhysicalChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
              </div>
            </div>
          )}
          
          {/* Health & Medical Data Tab */}
          {activeTab === 'health' && (
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900">Health Information</h2>
              <p className="text-sm text-gray-500">
                This helps us provide safe and appropriate exercise recommendations.
              </p>
              
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Health Conditions</h3>
                <p className="text-xs text-gray-500 mb-3">
                  Select any conditions that apply to you.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-center">
                    <input
                      id="heart-disease"
                      name="heartDisease"
                      type="checkbox"
                      checked={healthData.conditions.heartDisease}
                      onChange={() => handleHealthConditionChange('heartDisease')}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor="heart-disease" className="ml-2 block text-sm text-gray-700">
                      Heart Disease
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      id="high-blood-pressure"
                      name="highBloodPressure"
                      type="checkbox"
                      checked={healthData.conditions.highBloodPressure}
                      onChange={() => handleHealthConditionChange('highBloodPressure')}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor="high-blood-pressure" className="ml-2 block text-sm text-gray-700">
                      High Blood Pressure
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      id="diabetes"
                      name="diabetes"
                      type="checkbox"
                      checked={healthData.conditions.diabetes}
                      onChange={() => handleHealthConditionChange('diabetes')}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor="diabetes" className="ml-2 block text-sm text-gray-700">
                      Diabetes
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      id="arthritis"
                      name="arthritis"
                      type="checkbox"
                      checked={healthData.conditions.arthritis}
                      onChange={() => handleHealthConditionChange('arthritis')}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor="arthritis" className="ml-2 block text-sm text-gray-700">
                      Arthritis
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      id="respiratory-issues"
                      name="respiratoryIssues"
                      type="checkbox"
                      checked={healthData.conditions.respiratoryIssues}
                      onChange={() => handleHealthConditionChange('respiratoryIssues')}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor="respiratory-issues" className="ml-2 block text-sm text-gray-700">
                      Respiratory Issues
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      id="none"
                      name="none"
                      type="checkbox"
                      checked={healthData.conditions.none}
                      onChange={() => handleHealthConditionChange('none')}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor="none" className="ml-2 block text-sm text-gray-700">
                      None of the above
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="sleepHours" className="block text-sm font-medium text-gray-700">
                    Average Sleep Hours (per night)
                  </label>
                  <select
                    id="sleepHours"
                    name="sleepHours"
                    value={healthData.sleepHours}
                    onChange={handleHealthChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  >
                    {[...Array(12).keys()].map(i => (
                      <option key={i + 4} value={i + 4}>
                        {i + 4} hours
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label htmlFor="waterIntake" className="block text-sm font-medium text-gray-700">
                    Daily Water Intake (ml)
                  </label>
                  <input
                    type="number"
                    name="waterIntake"
                    id="waterIntake"
                    value={healthData.waterIntake}
                    onChange={handleHealthChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label htmlFor="medications" className="block text-sm font-medium text-gray-700">
                    Current Medications (optional)
                  </label>
                  <textarea
                    id="medications"
                    name="medications"
                    rows={2}
                    value={healthData.medications}
                    onChange={handleHealthChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                    placeholder="List any medications you are currently taking"
                  />
                </div>
                
                <div>
                  <label htmlFor="allergies" className="block text-sm font-medium text-gray-700">
                    Allergies (optional)
                  </label>
                  <textarea
                    id="allergies"
                    name="allergies"
                    rows={2}
                    value={healthData.allergies}
                    onChange={handleHealthChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                    placeholder="List any allergies you have"
                  />
                </div>
                
                <div>
                  <label htmlFor="injuries" className="block text-sm font-medium text-gray-700">
                    Previous Injuries (optional)
                  </label>
                  <textarea
                    id="injuries"
                    name="injuries"
                    rows={2}
                    value={healthData.injuries}
                    onChange={handleHealthChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                    placeholder="List any previous injuries that may affect your walking"
                  />
                </div>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-yellow-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">Medical Disclaimer</h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>
                        The recommendations provided by this app are not a substitute for professional medical advice.
                        Always consult your doctor before beginning any new exercise program.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Walking & Activity Preferences Tab */}
          {activeTab === 'preferences' && (
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900">Activity Preferences</h2>
              <p className="text-sm text-gray-500">
                Help us customize your walking experience.
              </p>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Preferred Time of Day
                  </label>
                  <div className="mt-2 flex flex-wrap gap-3">
                    <label className={`flex items-center justify-center p-3 border ${preferenceData.timePreference === 'morning' ? 'border-primary bg-green-50' : 'border-gray-300'} rounded-md cursor-pointer`}>
                      <input
                        type="radio"
                        name="timePreference"
                        value="morning"
                        checked={preferenceData.timePreference === 'morning'}
                        onChange={handlePreferenceChange}
                        className="sr-only"
                      />
                      <span className="text-sm font-medium">Morning</span>
                    </label>
                    
                    <label className={`flex items-center justify-center p-3 border ${preferenceData.timePreference === 'afternoon' ? 'border-primary bg-green-50' : 'border-gray-300'} rounded-md cursor-pointer`}>
                      <input
                        type="radio"
                        name="timePreference"
                        value="afternoon"
                        checked={preferenceData.timePreference === 'afternoon'}
                        onChange={handlePreferenceChange}
                        className="sr-only"
                      />
                      <span className="text-sm font-medium">Afternoon</span>
                    </label>
                    
                    <label className={`flex items-center justify-center p-3 border ${preferenceData.timePreference === 'evening' ? 'border-primary bg-green-50' : 'border-gray-300'} rounded-md cursor-pointer`}>
                      <input
                        type="radio"
                        name="timePreference"
                        value="evening"
                        checked={preferenceData.timePreference === 'evening'}
                        onChange={handlePreferenceChange}
                        className="sr-only"
                      />
                      <span className="text-sm font-medium">Evening</span>
                    </label>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Environment Preference
                  </label>
                  <div className="mt-2 flex flex-wrap gap-3">
                    <label className={`flex items-center justify-center p-3 border ${preferenceData.environmentPreference === 'outdoors' ? 'border-primary bg-green-50' : 'border-gray-300'} rounded-md cursor-pointer`}>
                      <input
                        type="radio"
                        name="environmentPreference"
                        value="outdoors"
                        checked={preferenceData.environmentPreference === 'outdoors'}
                        onChange={handlePreferenceChange}
                        className="sr-only"
                      />
                      <span className="text-sm font-medium">Outdoors</span>
                    </label>
                    
                    <label className={`flex items-center justify-center p-3 border ${preferenceData.environmentPreference === 'indoors' ? 'border-primary bg-green-50' : 'border-gray-300'} rounded-md cursor-pointer`}>
                      <input
                        type="radio"
                        name="environmentPreference"
                        value="indoors"
                        checked={preferenceData.environmentPreference === 'indoors'}
                        onChange={handlePreferenceChange}
                        className="sr-only"
                      />
                      <span className="text-sm font-medium">Indoors</span>
                    </label>
                    
                    <label className={`flex items-center justify-center p-3 border ${preferenceData.environmentPreference === 'mixed' ? 'border-primary bg-green-50' : 'border-gray-300'} rounded-md cursor-pointer`}>
                      <input
                        type="radio"
                        name="environmentPreference"
                        value="mixed"
                        checked={preferenceData.environmentPreference === 'mixed'}
                        onChange={handlePreferenceChange}
                        className="sr-only"
                      />
                      <span className="text-sm font-medium">Mixed</span>
                    </label>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Goal Type
                  </label>
                  <select
                    id="goalType"
                    name="goalType"
                    value={preferenceData.goalType}
                    onChange={handlePreferenceChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  >
                    <option value="steps">Daily Steps</option>
                    <option value="distance">Distance</option>
                    <option value="time">Active Time</option>
                    <option value="calories">Calories Burned</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Social Preference
                  </label>
                  <div className="mt-2 flex flex-wrap gap-3">
                    <label className={`flex items-center justify-center p-3 border ${preferenceData.socialPreference === 'solo' ? 'border-primary bg-green-50' : 'border-gray-300'} rounded-md cursor-pointer`}>
                      <input
                        type="radio"
                        name="socialPreference"
                        value="solo"
                        checked={preferenceData.socialPreference === 'solo'}
                        onChange={handlePreferenceChange}
                        className="sr-only"
                      />
                      <span className="text-sm font-medium">Solo Walker</span>
                    </label>
                    
                    <label className={`flex items-center justify-center p-3 border ${preferenceData.socialPreference === 'group' ? 'border-primary bg-green-50' : 'border-gray-300'} rounded-md cursor-pointer`}>
                      <input
                        type="radio"
                        name="socialPreference"
                        value="group"
                        checked={preferenceData.socialPreference === 'group'}
                        onChange={handlePreferenceChange}
                        className="sr-only"
                      />
                      <span className="text-sm font-medium">Group Walker</span>
                    </label>
                    
                    <label className={`flex items-center justify-center p-3 border ${preferenceData.socialPreference === 'both' ? 'border-primary bg-green-50' : 'border-gray-300'} rounded-md cursor-pointer`}>
                      <input
                        type="radio"
                        name="socialPreference"
                        value="both"
                        checked={preferenceData.socialPreference === 'both'}
                        onChange={handlePreferenceChange}
                        className="sr-only"
                      />
                      <span className="text-sm font-medium">Both</span>
                    </label>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Challenge Preference
                  </label>
                  <div className="mt-2 flex flex-wrap gap-3">
                    <label className={`flex items-center justify-center p-3 border ${preferenceData.challengePreference === 'casual' ? 'border-primary bg-green-50' : 'border-gray-300'} rounded-md cursor-pointer`}>
                      <input
                        type="radio"
                        name="challengePreference"
                        value="casual"
                        checked={preferenceData.challengePreference === 'casual'}
                        onChange={handlePreferenceChange}
                        className="sr-only"
                      />
                      <span className="text-sm font-medium">Casual</span>
                    </label>
                    
                    <label className={`flex items-center justify-center p-3 border ${preferenceData.challengePreference === 'competitive' ? 'border-primary bg-green-50' : 'border-gray-300'} rounded-md cursor-pointer`}>
                      <input
                        type="radio"
                        name="challengePreference"
                        value="competitive"
                        checked={preferenceData.challengePreference === 'competitive'}
                        onChange={handlePreferenceChange}
                        className="sr-only"
                      />
                      <span className="text-sm font-medium">Competitive</span>
                    </label>
                    
                    <label className={`flex items-center justify-center p-3 border ${preferenceData.challengePreference === 'both' ? 'border-primary bg-green-50' : 'border-gray-300'} rounded-md cursor-pointer`}>
                      <input
                        type="radio"
                        name="challengePreference"
                        value="both"
                        checked={preferenceData.challengePreference === 'both'}
                        onChange={handlePreferenceChange}
                        className="sr-only"
                      />
                      <span className="text-sm font-medium">Both</span>
                    </label>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Equipment
                  </label>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <input
                        id="smartwatch"
                        type="checkbox"
                        checked={preferenceData.equipment.smartwatch}
                        onChange={() => handleEquipmentChange('smartwatch')}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                      <label htmlFor="smartwatch" className="ml-2 block text-sm text-gray-700">
                        Smartwatch
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="fitnessTracker"
                        type="checkbox"
                        checked={preferenceData.equipment.fitnessTracker}
                        onChange={() => handleEquipmentChange('fitnessTracker')}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                      <label htmlFor="fitnessTracker" className="ml-2 block text-sm text-gray-700">
                        Fitness Tracker
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="smartphone"
                        type="checkbox"
                        checked={preferenceData.equipment.smartphone}
                        onChange={() => handleEquipmentChange('smartphone')}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                      <label htmlFor="smartphone" className="ml-2 block text-sm text-gray-700">
                        Smartphone
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="specializedFootwear"
                        type="checkbox"
                        checked={preferenceData.equipment.specializedFootwear}
                        onChange={() => handleEquipmentChange('specializedFootwear')}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                      <label htmlFor="specializedFootwear" className="ml-2 block text-sm text-gray-700">
                        Specialized Walking Footwear
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Save Button - Always visible */}
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={handleSaveSettings}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            <Save className="mr-2 h-4 w-4" />
            Save Settings
          </button>
        </div>
      </main>
    </div>
    <div className="md:hidden block sticky bottom-0 z-50 bg-white border-t border-gray-200 p-2">
        <Navigation orientation='horizontal' />
      </div>
    </div>
  );
};

export default UserSettings;